import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MapboxDrawService } from '../../../../auxiliares/mapbox/mapbox-draw.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { GrupoEntidadRegistry } from '../../../standalone/grupos/asignador/grupo-entidad.registry';
import {
  AsignarGrupoZonaDialogComponent,
  AsignarGrupoZonaData,
} from '../../../standalone/grupos/asignador/asignar-grupo-zona-dialog.component';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  ICliente,
  IFilter,
  IGateway,
  IGeoJSONPoint,
  ILayerMapaPersonalizado,
  ILuminaria,
  IPopulate,
  IQueryParam,
  IUbicacion,
  IUbicacionBase,
} from 'modelos/src';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { LoginService } from '../../../login/login.service';
import { LuminariasService } from '../service';
import { IEstadoLuminaria } from '../luminarias.types';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { LuminariasLayerManager } from '../../../../auxiliares/mapbox/entity-managers/luminarias-layer.manager';
import { PolygonConfig } from '../../../../auxiliares/mapbox/entity-managers/polygons-layer.manager';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { AccionPoligonoLuminarias } from './flotante-luminarias/flotante-luminarias.component';
import { ExportarLuminariasDialogComponent } from '../luminarias/exportar-luminarias-dialog/exportar-luminarias-dialog.component';
import { LuminariasHttpService } from '../../../../auxiliares/servicios/http/luminarias.service';
import { DescargasService } from '../../../navigation/descargas/service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { puntoValido } from '../../../../auxiliares/mapbox/ubicacion-valida';

@Component({
  selector: 'app-mapa-luminarias',
  templateUrl: './mapa-luminarias.component.html',
  styleUrl: './mapa-luminarias.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MapaLuminariasComponent implements OnInit, OnDestroy {
  private formularios = inject(FormularioDialogService);
  private lista2 = inject(Lista2Service);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private luminariasLayerManager = inject(LuminariasLayerManager);
  private luminariasService = inject(LuminariasService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private helper = inject(HelperService);
  private registryEntidades = inject(GrupoEntidadRegistry);
  private drawService = inject(MapboxDrawService);
  private luminariasHttp = inject(LuminariasHttpService);
  private descargas = inject(DescargasService);

  // Asignación de grupo por zona (dibujar polígono → asignar a las de adentro).
  // Usa el MapboxDrawService SINGLETON (patrón idéntico a mapa-alertas-seguridad,
  // que funciona): init lazy una sola vez + dibujar; parar/borrarTodo entre usos.
  private drawSub?: Subscription;
  private drawInicializado = false;
  private zonaEnProceso = false;
  /** Qué se eligió en el menú de acciones de polígono antes de dibujar. */
  private accionPoligono: AccionPoligonoLuminarias = 'cambiar-grupo';
  public modoAsignarZona = false;
  public soportaAsignarZona = this.registryEntidades.soportaGeo('Luminaria');

  public name = 'MapaLuminariasComponent';
  public showPopup = true;

  // Mapa
  public map: mapboxgl.Map | null = null;

  /// Datos de luminarias
  public luminaria?: ILuminaria;
  public luminarias?: ILuminaria[] = [];
  public luminariasSeleccionadas: ILuminaria[] = [];
  public luminarias$?: Subscription;
  public estado?: IEstadoLuminaria;

  //Listar gateways
  public gateway?: IGateway;
  public gateways?: IGateway[] = [];
  public gateways$?: Subscription;

  //Datos de clientes
  public clientes?: ICliente[] = [];
  public clientesSeleccionados?: ICliente[] = [];
  public clientePropio: ICliente = LoginService.getClientePropio();

  //Datos de zonas (ubicaciones categoría Luminarias)
  public zonas?: IUbicacionBase<'Luminarias'>[] = [];
  public zonas$?: Subscription;

  //Nombre del mapa
  public nombreMap = 'MapaLuminariasComponent';
  //Propiedades para zoom
  public currentZoom = 16;
  public minZoom = 1;
  public maxZoom = 20;

  //MAPAS A MOSTRAR
  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseLuminariasComponent'; //Key para ver el mapa base que está en el local Storage
  public tipoMapaBase: TipoMapaBase =
    localStorage.getItem(this.TIPO_MAPA_BASE_KEY) || 'cartodb'; //Mapa base que está en el local Storage
  public mapasPersonalizados: ILayerMapaPersonalizado[] = []; //Mapas personalizados del cliente

  //Propiedades para guardar la ubicación del mapa
  private debounceTimer: any;

  /// Consulta del mapa
  /**
   * El mapa pide **todas** las luminarias (sin tope: antes cortaba en 1000 y
   * el resto no se dibujaba ni se contaba) pero **sólo lo que dibuja, cuenta y
   * filtra**. El popup pide la luminaria entera al abrirse
   * (LuminariasService.luminariaParaPopup). Mismo criterio que FARO.
   */
  public populate: IPopulate[] = [
    // Buscador y filtro "Dispositivo" del flotante
    { path: 'dispositivo', select: 'deveui name' },
    // Buscador y filtro por grupo
    { path: 'grupos', select: 'nombre color' },
  ];
  public queryLuminarias: IQueryParam = {
    select:
      '_id identificacion deveui ubicacion estado direccion idsGrupos idCliente idPuesta idDispositivo tipoDispositivo fechaUltimaComunicacion',
    populate: JSON.stringify(this.populate),
    sort: '-fechaCreacion',
    unlimited: true,
    includeChildren: true,
    childrenLevel: 0,
  } as IQueryParam;

  /**
   * Las que tienen una ubicación real: las que se dibujan y se cuentan. Las
   * demás (sin coordenadas o con el valor por defecto de un selector de mapa)
   * sólo se cuentan en el aviso "sin ubicación" del flotante.
   */
  public luminariasConUbicacion: ILuminaria[] = [];
  public sinUbicacion = 0;

  /// Toggles
  public mostrarClusters = false; //Por defecto se muestran las luminarias desclusterizadas
  public mostrarPoligonosClientes = false; //Por defecto NO muestra los polígonos de clientes
  public mostrarPoligonosZonas = true; //Por defecto muestra los polígonos de zonas
  public mostrarTextoClientes = true; //Por defecto muestra el texto de los polígonos de clientes
  public mostrarTextoZonas = true; //Por defecto muestra el texto de los polígonos de zonas
  public mostrarGateways = true; //Por defecto se muestran los gateways

  // Entidades permitidas para re-aplicar datos al cambiar capa base. Las entidades pueden ser las que están presentes en MapaFacilV2Data
  public allowedTypes: string[] = ['luminarias', 'gateways'];

  /// Listado de luminarias
  public async listar(): Promise<void> {
    return new Promise((resolve) => {
      this.luminarias$?.unsubscribe();
      this.luminarias$ = this.lista2
        .luminarias(this.queryLuminarias, 30)
        .subscribe(async (data) => {
          this.luminarias = (data.datos ?? []) as ILuminaria[];
          this.luminariasConUbicacion = this.luminarias.filter(
            (l) => puntoValido(l.ubicacion) !== null,
          );
          this.sinUbicacion =
            this.luminarias.length - this.luminariasConUbicacion.length;
          this.luminariasSeleccionadas = this.luminariasConUbicacion;
          // OnPush: Marcar para change detection después del polling
          this.cdr.markForCheck();

          resolve();
        });
    });
  }

  /// Listado de gateways
  public async listarGateways(): Promise<void> {
    return new Promise((resolve) => {
      const filter: IFilter<{ 'ubicacion.coordinates': [number, number] }> = {
        'ubicacion.coordinates': { $exists: true, $ne: null },
      };
      const queryGateways: IQueryParam = {
        filter: JSON.stringify(filter),
        select:
          'gatewayEui nombre fechaCreacion nombreChirpstack fechaCreacionChirpstack ubicacion idCliente toaPromedioHora estadoSalud estadisticasActualizadas',
        sort: 'name',
        page: 0,
        includeChildren: true,
      };
      this.gateways$?.unsubscribe();
      this.gateways$ = this.lista2
        .gateways(queryGateways, 30)
        .subscribe(async (data) => {
          this.gateways = data.datos;
          // OnPush: Marcar para change detection después del polling
          this.cdr.markForCheck();
          resolve();
        });
    });
  }

  private async _listarClientes() {
    const filter: IFilter<ICliente> = {
      'config.moduloLuminarias.activo': true,
    } as any;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'nombre poligono config nivel idPadre mapLayers',
      sort: 'nombre',
      page: 0,
      includeChildren: true,
    };
    const clientes = await firstValueFrom(this.lista2.clientes(query));
    return clientes.datos;
  }

  //Listado de clientes (para obtener los polígonos)
  public async listarClientes(): Promise<void> {
    // No los traigo de cache porque no tiene el poligono
    this.clientes = await this._listarClientes();
    this.actualizarPoligonos();
  }

  //Listado de zonas (ubicaciones categoría Luminarias, para sus polígonos)
  public async listarZonas(): Promise<void> {
    return new Promise((resolve) => {
      const filter: IFilter<IUbicacion> = {
        categoria: 'Luminarias',
        'geojson.coordinates': { $exists: true, $ne: null },
      } as any;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'identificacion color geojson idCliente categoria',
        sort: 'identificacion',
        page: 0,
        includeChildren: true,
      };
      this.zonas$?.unsubscribe();
      this.zonas$ = this.lista2
        .ubicacions<'Luminarias'>(query, 30)
        .subscribe((data) => {
          this.zonas = data.datos;
          this.actualizarPoligonos();
          this.cdr.markForCheck();
          resolve();
        });
    });
  }

  /// Agrupa y coloca los marcadores en capas individuales y en cluster
  private addMarkers() {
    if (!this.mapaFacilV2Service.isReady()) return;

    const luminariasAMostrar = this.luminariasSeleccionadas;

    // API simplificada: pasamos las luminarias directamente
    this.mapaFacilV2Service.updateData({
      luminarias: luminariasAMostrar,
    });

    // Agregar gateways si está habilitado
    const gatewaysAMostrar = this.mostrarGateways ? this.gateways || [] : [];
    this.mapaFacilV2Service.updateData({
      gateways: gatewaysAMostrar,
    });
  }

  public toggleMostrarClusters() {
    this.mostrarClusters = !this.mostrarClusters;
  }

  //Muestra o no los clusters
  public onClusterToggle(clusterizar: boolean) {
    this.mostrarClusters = clusterizar;
    this.mapaFacilV2Service.setClusteringEnabled(
      'luminarias',
      this.mostrarClusters,
    );
    this.mapaFacilV2Service.setClusteringEnabled(
      'gateways',
      this.mostrarClusters,
    );
  }

  //Muestra o no los polígonos de clientes
  public onPoligonoClientesToggle(mostrar: boolean) {
    this.mostrarPoligonosClientes = mostrar;
    this.actualizarPoligonos();
  }

  //Muestra o no los polígonos de zonas
  public onPoligonoZonasToggle(mostrar: boolean) {
    this.mostrarPoligonosZonas = mostrar;
    this.actualizarPoligonos();
  }

  //Muestra o no el texto de los polígonos de clientes
  public onTextoClientesToggle(mostrar: boolean) {
    this.mostrarTextoClientes = mostrar;
    this.actualizarPoligonos();
  }

  //Muestra o no el texto de los polígonos de zonas
  public onTextoZonasToggle(mostrar: boolean) {
    this.mostrarTextoZonas = mostrar;
    this.actualizarPoligonos();
  }

  public async onLuminariaChange(l: ILuminaria[]) {
    this.luminariasSeleccionadas = l;
    this.addMarkers();
  }

  //Muestra o no los gateways
  public onGatewayToggle(mostrarGateways: boolean) {
    this.mostrarGateways = mostrarGateways;
    localStorage.setItem(
      'mostrarGatewaysLuminaria',
      mostrarGateways.toString(),
    );
    this.addMarkers();
  }

  // Construye y aplica los polígonos a mostrar: clientes y/o zonas, según sus toggles
  private actualizarPoligonos(): void {
    if (!this.mapaFacilV2Service.isReady()) return;

    const polygons: PolygonConfig[] = [];

    // Polígonos de clientes
    if (this.mostrarPoligonosClientes) {
      const clientesAMostrar = this.clientesSeleccionados?.length
        ? this.clientesSeleccionados
        : this.clientes;

      for (const cliente of clientesAMostrar ?? []) {
        if (
          cliente.poligono &&
          cliente.poligono.type === 'MultiPolygon' &&
          Array.isArray(cliente.poligono.coordinates)
        ) {
          const color =
            cliente.config?.tema?.primaryColor ||
            this.clientePropio?.config?.tema?.primaryColor ||
            '#2196F3'; // Color azul por defecto
          for (const polygonCoords of cliente.poligono.coordinates) {
            polygons.push({
              points: polygonCoords[0] as [number, number][], // MultiPolygon tiene un nivel extra de anidación
              color,
              fillColor: color,
              opacity: 0.3,
              label: this.mostrarTextoClientes ? cliente.nombre : undefined,
            });
          }
        }
      }
    }

    // Polígonos de zonas (ubicaciones categoría Luminarias)
    if (this.mostrarPoligonosZonas) {
      for (const zona of this.zonas ?? []) {
        const geojson = zona.geojson;
        if (!geojson) continue;
        const color = zona.color || '#ffcc33'; // Color amarillo por defecto

        const label = this.mostrarTextoZonas ? zona.identificacion : undefined;
        if (geojson.type === 'Polygon') {
          polygons.push({
            points: geojson.coordinates[0] as [number, number][],
            color,
            fillColor: color,
            opacity: 0.3,
            label,
          });
        } else if (geojson.type === 'MultiPolygon') {
          for (const poly of geojson.coordinates) {
            polygons.push({
              points: poly[0] as [number, number][],
              color,
              fillColor: color,
              opacity: 0.3,
              label,
            });
          }
        }
      }
    }

    this.mapaFacilV2Service.updateData({
      polygons,
    });
  }

  public onClienteSeleccionadoChange(seleccionados: ICliente[]) {
    this.clientesSeleccionados = seleccionados;
    this.actualizarPoligonos();
  }

  //Procesamiento de lo guardado en el local storage
  private getBooleanFromStorage(key: string, defaultValue: boolean): boolean {
    const value = localStorage.getItem(key);
    return value !== null ? value === 'true' : defaultValue;
  }

  /**
   * Callback cuando el mapa está listo
   */
  public onMapReady(map: mapboxgl.Map): void {
    // Guardar la instancia para features que operan sobre el mapa (ej. dibujo
    // de polígono para asignar grupo por zona).
    this.map = map;
    // IMPORTANTE: Configurar luminarias para usar mapa específico (iconos .webp dinámicos)
    this.luminariasLayerManager.setMapaEspecifico(true);

    // Habilitar/deshabilitar clustering según estado guardado
    if (this.mostrarClusters) {
      this.mapaFacilV2Service.setClusteringEnabled('luminarias', true);
      this.mapaFacilV2Service.setClusteringEnabled('gateways', true);
    }

    // Volver a cargar datos que ya están en memoria
    this.addMarkers();
    this.actualizarPoligonos();
    // OnPush: Marcar para check por si el mapa se carga después del polling
    this.cdr.markForCheck();
  }

  // ── Asignar grupo por zona ──────────────────────────────────────────────────
  // Activa/desactiva el modo dibujo de polígono sobre el mapa. Al cerrar un
  // polígono se abre el dialog que asigna el grupo elegido a las luminarias de
  // adentro (reusa AsignacionGrupoService → mismo PUT → dispara el sync).
  // Entrada desde el flotante: "Acciones de polígono".
  // El menú de acciones de polígono elige QUÉ hacer con las luminarias de
  // adentro; el dibujo es el mismo para todas las acciones.
  public onAccionPoligono(accion: AccionPoligonoLuminarias): void {
    this.accionPoligono = accion;
    this.iniciarDibujoZona();
  }

  // Inicia el dibujo de polígono. Patrón idéntico a mapa-alertas-seguridad
  // (que funciona): init lazy UNA sola vez — mapbox-gl-draw se rompe con
  // addControl/removeControl repetido sobre el mapa reusado por mapId — y entre
  // dibujos solo borrarTodo(). El cleanup completo va en ngOnDestroy.
  public iniciarDibujoZona(): void {
    if (this.modoAsignarZona) return; // ya dibujando
    // El evento (mapReady) del <app-mapa-facil-v2> puede no llegar a este
    // componente (timing/reuso de ruta), dejando this.map en null y el dibujo
    // sin arrancar. El mapa real vive en el servicio singleton → tomarlo de ahí.
    const map = this.map ?? this.mapaFacilV2Service.getMap();
    if (!map) {
      this.helper.notifError('El mapa todavía no está listo');
      return;
    }
    this.map = map;
    this.zonaEnProceso = false;

    if (!this.drawInicializado) {
      try {
        this.drawService.initDrawControls(map);
        this.drawInicializado = true;
      } catch (err) {
        // Si el source ya existe (init previo sobre el mapa reusado), seguir
        // adelante reusando el draw existente — igual que alertas.
        const msg = String((err as Error)?.message || '');
        if (msg.includes('already a source')) {
          this.drawInicializado = true;
        } else {
          this.helper.notifError(err);
          return;
        }
      }
    } else {
      // Limpiar geometrías previas antes de re-dibujar
      try {
        this.drawService.borrarTodo();
      } catch {
        /* noop */
      }
    }

    this.drawSub?.unsubscribe();
    this.drawSub = this.drawService.multiPolygon.subscribe((polys) => {
      if (!polys?.length || this.zonaEnProceso) return; // ignora undefined inicial
      this.zonaEnProceso = true;
      const poligono = polys[0] as GeoJSON.Position[][];
      if (this.accionPoligono === 'exportar-excel') {
        this.exportarPoligono(poligono);
      } else {
        this.abrirDialogZona(poligono);
      }
    });
    this.drawService.dibujar('Polygon');
    this.modoAsignarZona = true;
    this.helper.notifSuccess(
      this.accionPoligono === 'exportar-excel'
        ? 'Dibujá un polígono (doble clic para cerrar) para exportar las luminarias de adentro'
        : 'Dibujá un polígono (doble clic para cerrar) para cambiarles el grupo',
    );
    this.cdr.markForCheck();
  }

  private abrirDialogZona(coordinates: GeoJSON.Position[][]): void {
    // Los eventos de Draw corren fuera de Angular zone.
    this.ngZone.run(() => {
      const data: AsignarGrupoZonaData = {
        categoria: 'Luminaria',
        polygon: { type: 'Polygon', coordinates },
        idCliente: this.clientePropio?._id,
        etiqueta: 'luminarias',
        // Solo las luminarias que sobreviven a los filtros activos (las que se
        // están mostrando como markers) entran en la asignación por polígono.
        idsFiltrados: (this.luminariasSeleccionadas ?? [])
          .map((l) => l._id)
          .filter((id): id is string => !!id),
      };
      const ref = this.formularios.abrir(AsignarGrupoZonaDialogComponent, {
        tamano: 'mediano',
        data,
      });
      ref.afterClosed().subscribe((n?: number) => {
        this.pararDibujo();
        // Si se aplicó a alguna, recargar luminarias y re-renderizar markers para
        // que los popups reflejen los grupos nuevos (la data del mapa está
        // cacheada del polling).
        if (n) void this.listar().then(() => this.addMarkers());
      });
    });
  }

  /**
   * Exporta a Excel las luminarias que quedaron dentro del polígono. Mismo
   * criterio que el cambio de grupo: solo las que sobreviven a los filtros
   * activos (las que se están mostrando como markers).
   *
   * Va por job asíncrono: el scope puede ser grande y las hojas históricas
   * traen el mes completo.
   */
  private exportarPoligono(coordinates: GeoJSON.Position[][]): void {
    this.ngZone.run(async () => {
      const poligono = turf.polygon(coordinates);
      const dentro = (this.luminariasSeleccionadas ?? []).filter((l) => {
        // Las mismas que se ven en el mapa: sin la ubicación por defecto.
        const punto = puntoValido(l.ubicacion);
        if (!punto) return false;
        return turf.booleanPointInPolygon(turf.point(punto), poligono);
      });
      this.pararDibujo();

      if (!dentro.length) {
        this.helper.notifWarn('No hay luminarias dentro del polígono');
        return;
      }

      const config = await firstValueFrom(
        this.formularios
          .abrir(ExportarLuminariasDialogComponent, {
            tamano: 'chico',
            data: { nivel: 'luminaria' },
          })
          .afterClosed(),
      );
      if (!config) return;

      const ids = dentro.map((l) => l._id).filter((id): id is string => !!id);
      const fecha = new Date().toISOString().slice(0, 10);
      const nombre = `luminarias_poligono_${ids.length}_${fecha}`;
      const scope = {
        query: { filter: JSON.stringify({ _id: { $in: ids } }) } as IQueryParam,
      };

      this.descargas.agregarDescargaJob(nombre, {
        crearJob: () =>
          this.luminariasHttp.crearExportJob(scope, config, `${nombre}.xlsx`),
        consultarEstado: (jobId) => this.luminariasHttp.getExportJob(jobId),
        descargarArchivo: (jobId) =>
          this.luminariasHttp.descargarExportJob(jobId),
      });
      this.helper.notifSuccess(
        `Exportación iniciada (${ids.length} luminarias) — ver panel de Descargas`,
      );
    });
  }

  // Detiene el dibujo (parar+borrarTodo), SIN remover el control — idéntico a
  // alertas. El cleanup() completo va en ngOnDestroy.
  private pararDibujo(): void {
    this.drawSub?.unsubscribe();
    this.drawSub = undefined;
    this.zonaEnProceso = false;
    if (this.drawInicializado) {
      try {
        this.drawService.parar();
        this.drawService.borrarTodo();
      } catch {
        /* noop */
      }
    }
    this.modoAsignarZona = false;
    this.cdr.markForCheck();
  }

  /// Ciclo de vida
  async ngOnInit() {
    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }
    this.mostrarClusters = this.getBooleanFromStorage(
      'mostrarClusterLuminaria',
      false,
    );
    this.mostrarPoligonosClientes = this.getBooleanFromStorage(
      'mostrarPoligonoClientesLuminaria',
      false,
    );
    this.mostrarPoligonosZonas = this.getBooleanFromStorage(
      'mostrarPoligonoZonasLuminaria',
      true,
    );
    this.mostrarTextoClientes = this.getBooleanFromStorage(
      'mostrarTextoPoligonoClientesLuminaria',
      true,
    );
    this.mostrarTextoZonas = this.getBooleanFromStorage(
      'mostrarTextoPoligonoZonasLuminaria',
      true,
    );
    this.mostrarGateways = this.getBooleanFromStorage(
      'mostrarGatewaysLuminaria',
      true,
    );

    await Promise.all([
      this.listar(),
      this.listarClientes(),
      this.listarZonas(),
      this.listarGateways(),
    ]);
  }

  ngOnDestroy() {
    // Limpiar el timer de debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    // Teardown del dibujo al salir del mapa.
    this.drawSub?.unsubscribe();
    if (this.drawInicializado) {
      try {
        this.drawService.cleanup();
      } catch {
        /* noop */
      }
    }
    this.luminarias$?.unsubscribe();
    this.gateways$?.unsubscribe();
    this.zonas$?.unsubscribe();

    // IMPORTANTE: Limpiar datos del servicio para evitar que persistan al cambiar de mapa
    // El servicio es singleton, por lo que mantiene datos entre componentes

    this.mapaFacilV2Service.clearAllData();
  }
}
